import { App } from 'antd';
import { useCallback, useRef, useState } from 'react';
import { ASRService } from '@/services/asrService';

export type ASRStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'recording'
  | 'error';

interface UseWebSocketASROptions {
  onTranscript?: (text: string, isFinal: boolean) => void;
  onError?: (error: Error) => void;
  onEnd?: () => void;
  onStart?: () => void;
  autoReconnect?: boolean; // 是否自动重连
  maxReconnectAttempts?: number; // 最大重连次数
}

const ERROR_WORDS = ['识别失败'];

/**
 * WebSocket ASR 语音识别 Hook
 *
 * 职责：
 * - 管理 ASR 服务实例生命周期
 * - 提供 React 状态管理
 * - 处理连接状态和错误
 * - 提供重连机制
 * - 友好的用户提示
 */
export const useWebSocketASR = (options: UseWebSocketASROptions = {}) => {
  const { message: appMessage } = App.useApp();
  const asrService = useRef<ASRService | null>(null);

  // 状态管理
  const [status, setStatus] = useState<ASRStatus>('idle');
  const [error, setError] = useState<Error | null>(null);
  const reconnectAttempts = useRef(0);
  const isManualStop = useRef(false);

  const {
    onTranscript,
    onError,
    onEnd,
    onStart,
    autoReconnect = false,
    maxReconnectAttempts = 3,
  } = options;

  // 使用 ref 存储 connect 函数，避免循环依赖
  const connectRef = useRef<(() => Promise<boolean>) | null>(null);

  /**
   * 处理错误
   */
  const handleError = useCallback(
    (err: Error) => {
      console.error('[WebSocket ASR] 错误:', err);
      setStatus('error');
      setError(err);

      // 错误分类提示
      const errorMsg = err.message;
      if (errorMsg.includes('permission') || errorMsg.includes('麦克风')) {
        appMessage.error('请授予麦克风权限');
      } else if (
        errorMsg.includes('WebSocket') ||
        errorMsg.includes('connection')
      ) {
        appMessage.error('网络连接失败，请检查网络');

        // 自动重连逻辑
        if (
          autoReconnect &&
          !isManualStop.current &&
          reconnectAttempts.current < maxReconnectAttempts
        ) {
          reconnectAttempts.current++;
          const delay = Math.min(
            1000 * 2 ** (reconnectAttempts.current - 1),
            10000,
          );
          console.log(
            `[WebSocket ASR] 将在 ${delay}ms 后尝试第 ${reconnectAttempts.current} 次重连`,
          );
          setTimeout(() => {
            connectRef.current?.();
          }, delay);
        }
      } else {
        appMessage.error(`语音识别错误：${errorMsg}`);
      }

      onError?.(err);
    },
    [appMessage, onError, autoReconnect, maxReconnectAttempts],
  );

  /**
   * 连接 WebSocket（不开始录音）
   */
  const connect = useCallback(async () => {
    // 检查浏览器支持
    if (!ASRService.isSupported()) {
      const err = new Error('浏览器不支持语音识别功能');
      handleError(err);
      return false;
    }

    // 如果已经连接，直接返回
    if (asrService.current?.isConnected()) {
      console.log('[WebSocket ASR] Already connected');
      return true;
    }

    try {
      isManualStop.current = false;
      setStatus('connecting');
      setError(null);

      console.log('[WebSocket ASR] 开始连接...');

      // 创建 ASR 服务实例
      if (!asrService.current) {
        asrService.current = new ASRService();
      }

      // 只连接 WebSocket，不开始录音
      await asrService.current.connect({
        onStart: () => {
          onStart?.();
          setStatus('recording');
          reconnectAttempts.current = 0; // 重置重连计数
        },
        onTranscript: (text, isFinal) => {
          console.log('[WebSocket ASR] 识别结果:', text, '是否最终:', isFinal);

          // 检查错误词
          if (
            ERROR_WORDS.some((word) =>
              text.toLowerCase().includes(word.toLowerCase()),
            )
          ) {
            console.error('[WebSocket ASR] 识别错误:', text);
            return;
          }

          onTranscript?.(text, isFinal);
        },
        onError: (error) => {
          handleError(error);
        },
        onEnd: () => {
          console.log('[WebSocket ASR] 识别结束');
          // 不改变状态，保持连接
          setStatus('connected');
          onEnd?.();
        },
        onDisconnect: () => {
          setStatus('idle');
        },
      });

      setStatus('connected');
      console.log('[WebSocket ASR] WebSocket 连接成功');
      return true;
    } catch (error) {
      console.error('[WebSocket ASR] 连接失败:', error);
      handleError(error as Error);
      return false;
    }
  }, [status, onTranscript, onEnd, handleError]);

  // 保存 connect 到 ref，供 handleError 使用
  connectRef.current = connect;

  /**
   * 断开 WebSocket 连接
   */
  const disconnect = useCallback(() => {
    console.log('[WebSocket ASR] 断开连接');
    isManualStop.current = true;
    reconnectAttempts.current = 0;

    if (asrService.current) {
      asrService.current.disconnect();
      setError(null);
    }
  }, []);

  /**
   * 开始录音（使用已有连接）
   */
  const startRecording = useCallback(async () => {
    if (!asrService.current?.isConnected()) {
      console.error('[WebSocket ASR] Not connected');
      appMessage.error('WebSocket 未连接');
      return false;
    }

    try {
      await asrService.current.startRecording();
      setStatus('recording');
      console.log('[WebSocket ASR] 开始录音');
      return true;
    } catch (error) {
      console.error('[WebSocket ASR] 启动录音失败:', error);
      handleError(error as Error);
      return false;
    }
  }, [handleError, appMessage]);

  /**
   * 停止录音（保持连接）
   */
  const stopRecording = useCallback(() => {
    console.log('[WebSocket ASR] 停止录音');

    if (asrService.current) {
      asrService.current.stopRecording();
    }
  }, []);

  /**
   * 清理资源
   */
  const cleanup = useCallback(() => {
    console.log('[WebSocket ASR] 清理资源');
    isManualStop.current = true;
    reconnectAttempts.current = 0;

    if (asrService.current) {
      asrService.current.cleanup();
      asrService.current = null;
    }

    setStatus('idle');
    setError(null);
  }, []);

  /**
   * 重置状态（用于手动重连）
   */
  const reset = useCallback(() => {
    console.log('[WebSocket ASR] 重置状态');
    cleanup();
    reconnectAttempts.current = 0;
  }, [cleanup]);

  /**
   * 检查浏览器支持
   */
  const isSupported = useCallback(() => {
    return ASRService.isSupported();
  }, []);

  return {
    // 连接管理
    connect,
    disconnect,

    // 录音控制
    startRecording,
    stopRecording,

    // 其他方法
    cleanup,
    reset,
    isSupported,

    // 状态
    status,
    error,
    isConnecting: status === 'connecting',
    isConnected: status === 'connected' || status === 'recording',
    isRecording: status === 'recording',
    hasError: status === 'error',
  };
};
